package pl.nickers.match3

import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow

/** Why gems were credited. Kept for analytics (E22) and for earn feedback in the UI. */
enum class GemEarnReason {
    STARTER_GRANT,
    FIRST_LEVEL_WIN,

    /** TODO(E9 stars, issue #3): unused until star tracking exists. */
    STAR_MILESTONE,

    /** E21 gem-pack purchase. */
    PURCHASE,
}

/** Why gems were debited. Every sink an epic adds gets an entry here. */
enum class GemSpendReason {
    /** E17 — instant life refill. */
    LIFE_REFILL,

    /** E19 — continue after a loss. */
    CONTINUE,

    /** E20 — booster bought from the shop. */
    BOOSTER,
}

/**
 * A credit that just happened, published for transient UI feedback ("+5 💎").
 *
 * [sequence] increments on every credit so the UI can tell two identical grants
 * apart and re-trigger its animation.
 */
data class GemEarnEvent(
    val amount: Int,
    val reason: GemEarnReason,
    val balanceAfter: Int,
    val sequence: Long,
)

/**
 * E18/F18.1 — the single choke point for every gem mutation in the game.
 *
 * Nothing else may write [GameStorage.gems]: composables render [balance], ECS
 * systems never touch gems at all, and the earn/spend epics (E17 lives, E19
 * continue, E20 boosters, E21 packs) go through [credit]/[debit] with a reason.
 * Amounts always come from [EconomyConfig]; no caller passes a literal.
 *
 * Modeled on [JourneyService]: a plain class over [GameStorage], no Compose or
 * UI imports, fully unit-testable. The in-memory [balance] is the authority for
 * readers and is written through to storage on every mutation, so a restart
 * rehydrates the same value.
 */
class WalletService(private val storage: GameStorage) {

    private val _balance = MutableStateFlow(storage.gems)

    /** Current gem balance. Never negative. */
    val balance: StateFlow<Int> = _balance.asStateFlow()

    private val _lastEarn = MutableStateFlow<GemEarnEvent?>(null)

    /**
     * The most recent credit, or null once the UI has shown it (see
     * [consumeEarnFeedback]). Debits deliberately publish nothing here: the
     * screens that spend gems (E17/E19/E20) show their own confirmation.
     */
    val lastEarn: StateFlow<GemEarnEvent?> = _lastEarn.asStateFlow()

    private var earnSequence: Long = 0

    /** Convenience for non-observing callers (tests, one-shot reads). */
    fun balance(): Int = _balance.value

    /**
     * Adds [amount] gems and returns the new balance. Non-positive amounts are
     * ignored (a "reward" of zero is a config mistake, not a runtime error).
     */
    fun credit(amount: Int, reason: GemEarnReason): Int {
        if (amount <= 0) return _balance.value
        val newBalance = _balance.value + amount
        persist(newBalance)
        earnSequence += 1
        _lastEarn.value = GemEarnEvent(
            amount = amount,
            reason = reason,
            balanceAfter = newBalance,
            sequence = earnSequence,
        )
        return newBalance
    }

    /**
     * Removes [amount] gems if the player can afford it.
     *
     * Returns false — leaving the balance untouched — when funds are short or
     * [amount] is negative. Callers branch on the boolean; insufficient funds is
     * an ordinary outcome (it routes to the E21 gem-pack sheet), never an
     * exception and never a partial debit.
     */
    fun debit(amount: Int, reason: GemSpendReason): Boolean {
        if (amount < 0) return false
        if (amount > _balance.value) return false
        persist(_balance.value - amount)
        return true
    }

    /** Clears the pending earn feedback once the UI has displayed it. */
    fun consumeEarnFeedback() {
        _lastEarn.value = null
    }

    /**
     * F18.2 — pays [EconomyConfig.STARTER_GRANT] the first time the game is ever
     * launched. Idempotent across restarts: the payout is flagged in storage, so
     * calling this on every launch (which the app does) grants exactly once.
     *
     * Returns true only on the launch that actually paid out.
     */
    fun grantStarterGemsIfNeeded(): Boolean {
        if (storage.starterGrantPaid) return false
        storage.starterGrantPaid = true
        credit(EconomyConfig.STARTER_GRANT, GemEarnReason.STARTER_GRANT)
        return true
    }

    /**
     * F18.2 — pays [EconomyConfig.FIRST_WIN_BONUS] the first time [levelId] is
     * won. Replays earn nothing: paid-out level ids are persisted, so the bonus
     * survives restarts and cannot be farmed by re-playing an easy level.
     *
     * Returns true only on the win that actually paid out.
     */
    fun awardFirstWinBonus(levelId: Int): Boolean {
        // ASSUMPTION: only journey levels earn this bonus. Arcade runs have no
        // level id and test levels are dev tooling, so both are filtered out by
        // the caller (GameViewModel) rather than here.
        if (levelId in storage.gemAwardedLevelIds) return false
        storage.gemAwardedLevelIds = storage.gemAwardedLevelIds + levelId
        credit(EconomyConfig.FIRST_WIN_BONUS, GemEarnReason.FIRST_LEVEL_WIN)
        return true
    }

    // TODO(E9 stars, issue #3): awardStarMilestones(totalStars) — credit
    // EconomyConfig.STAR_MILESTONE_BONUS for every EconomyConfig.STAR_MILESTONE_SIZE
    // boundary crossed since the last payout, with the last-paid milestone
    // persisted in GameStorage the same way starterGrantPaid is.

    private fun persist(newBalance: Int) {
        storage.gems = newBalance
        _balance.value = newBalance
    }
}
